import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Image as ImageIcon, Send, Loader2, RefreshCw, Mail, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailCampaigns = () => {
  const [topic, setTopic] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [subject, setSubject] = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [preheader, setPreheader] = useState('');
  const [body, setBody] = useState('');
  const [bannerId, setBannerId] = useState(null);
  const [bannerUrl, setBannerUrl] = useState('');

  const [recipients, setRecipients] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const [consent, setConsent] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [result, setResult] = useState(null);

  const generateText = async () => {
    if (!topic.trim()) { toast.error('Enter a topic first'); return; }
    setGenLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/generate-text`, { topic }, { withCredentials: true });
      setSubject(data.subject || '');
      setSubjectOptions(data.subject_options || []);
      setPreheader(data.preheader || '');
      setBody(data.body || '');
      toast.success('Email drafted — edit anything you like');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setGenLoading(false);
  };

  const generateBanner = async () => {
    if (!topic.trim()) { toast.error('Enter a topic first'); return; }
    setBannerLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/campaigns/generate-banner`, { topic }, { withCredentials: true });
      setBannerId(data.banner_id);
      setBannerUrl(`${API.replace('/api','')}${data.url}?t=${Date.now()}`);
      toast.success('Banner ready');
    } catch (e) { toast.error(e.response?.data?.detail || 'Image generation failed'); }
    setBannerLoading(false);
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error('Image too large (max 6MB)'); return; }
    setBannerLoading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await axios.post(`${API}/admin/campaigns/upload-banner`, { data: dataUrl, mime: file.type }, { withCredentials: true });
      setBannerId(data.banner_id);
      setBannerUrl(`${API.replace('/api','')}${data.url}?t=${Date.now()}`);
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setBannerLoading(false);
  };

  const doSend = async (isTest) => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required'); return; }
    if (isTest && !testEmail.trim()) { toast.error('Enter a test email'); return; }
    if (!isTest && !consent) { toast.error('Please confirm you have consent to email this list'); return; }
    if (!isTest && !window.confirm('Send this campaign to all recipients now?')) return;
    setSending(true); setResult(null);
    try {
      const payload = {
        subject, preheader, body, banner_id: bannerId,
        recipients: recipients.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean),
        include_past_customers: includePast, consent, is_test: isTest, test_email: testEmail,
      };
      const { data } = await axios.post(`${API}/admin/campaigns/send`, payload, { withCredentials: true });
      setResult(data);
      toast.success(isTest ? `Test sent to ${testEmail}` : `Campaign sent to ${data.sent} recipient(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-6" data-testid="email-campaigns-panel">
      <div className="flex items-center gap-2 mb-1"><Mail className="w-5 h-5 text-primary" /><h3 className="text-xl font-bold">Email Campaigns</h3></div>
      <p className="text-sm text-muted-foreground mb-6">Enter a topic, let AI draft the email + banner, edit anything, send a test to yourself, then send to your list. Every email includes an unsubscribe link and your company address.</p>

      <div className="mb-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 leading-relaxed" data-testid="campaign-privacy-note">
        🔒 <b>Private &amp; personalised:</b> each email is sent individually — no recipient ever sees another's address. Use <code>[First Name]</code> anywhere in the subject or body and it's replaced with each customer's real name (past customers are matched automatically; others get a friendly "there").
      </div>

      {/* Topic + generate */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Campaign topic, e.g. Announce IIY Rewards loyalty scheme" className="flex-1 px-4 py-2.5 rounded-xl border border-border" data-testid="campaign-topic" />
        <button onClick={generateText} disabled={genLoading} className="px-4 py-2.5 rounded-xl bg-primary text-white font-medium flex items-center gap-2 justify-center disabled:opacity-60" data-testid="campaign-generate-text">
          {genLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate email
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: editable content */}
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-border mt-1" data-testid="campaign-subject" />
            {subjectOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {subjectOptions.map((o, i) => (
                  <button key={i} onClick={() => setSubject(o)} className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid={`campaign-subject-option-${i}`}>{o}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Preheader</label>
            <input value={preheader} onChange={(e) => setPreheader(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-border mt-1" data-testid="campaign-preheader" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Body (editable)</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="w-full px-4 py-3 rounded-xl border border-border mt-1 font-mono text-sm" data-testid="campaign-body" />
          </div>
        </div>

        {/* Right: banner + recipients + send */}
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">Banner image</label>
              <div className="flex gap-2">
                <button onClick={generateBanner} disabled={bannerLoading} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-700 text-white flex items-center gap-1 disabled:opacity-60" data-testid="campaign-generate-banner">
                  {bannerLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />} {bannerUrl ? 'Regenerate' : 'Generate'}
                </button>
                <label className="text-xs px-3 py-1.5 rounded-lg bg-white border border-border cursor-pointer flex items-center gap-1" data-testid="campaign-upload-banner-label">
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(e.target.files?.[0])} data-testid="campaign-upload-banner" />
                </label>
                {bannerUrl && <button onClick={() => { setBannerUrl(''); setBannerId(null); }} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 border border-border" data-testid="campaign-remove-banner">Remove</button>}
              </div>
            </div>
            <div className="mt-1 rounded-xl border border-dashed border-border bg-gray-50 min-h-[120px] flex items-center justify-center overflow-hidden">
              {bannerUrl ? <img src={bannerUrl} alt="banner" className="w-full h-auto" data-testid="campaign-banner-img" /> : <span className="text-xs text-muted-foreground p-6">No banner — text-only email</span>}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Recipients (paste emails, comma or new line separated)</label>
            <textarea value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={4} placeholder="jane@example.com, john@example.com" className="w-full px-4 py-2.5 rounded-xl border border-border mt-1 text-sm" data-testid="campaign-recipients" />
            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer" data-testid="campaign-include-past">
              <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} className="w-4 h-4 accent-emerald-700" />
              Also include all past customers &amp; account holders
            </label>
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="campaign-consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-amber-600" />
            <span>I confirm I have consent / legitimate interest to send marketing emails to these recipients (UK PECR/GDPR).</span>
          </label>

          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-border">
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm" data-testid="campaign-test-email" />
            <button onClick={() => doSend(true)} disabled={sending} className="px-4 py-2.5 rounded-xl bg-white border border-border font-medium flex items-center gap-2 justify-center disabled:opacity-60" data-testid="campaign-test-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
            </button>
          </div>
          <button onClick={() => doSend(false)} disabled={sending} className="w-full px-4 py-3 rounded-xl bg-primary text-white font-bold flex items-center gap-2 justify-center disabled:opacity-60" data-testid="campaign-send">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send campaign
          </button>

          {result && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" data-testid="campaign-result">
              {result.test ? `Test sent to ${testEmail}.` : `Sent to ${result.sent} of ${result.recipients} recipient(s)${result.failed ? `, ${result.failed} failed` : ''}.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
